import React from 'react';
// third-party packages
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router';
// global state, action, API
import { getCurrentUser } from 'api/AuthAPI';
import useAuth, { AuthProvider } from 'contexts/AuthContext';
import authAction from 'actions/AuthAction';
// material UI
import CssBaseline from '@mui/material/CssBaseline'; // reset css
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
// custom packages
import PrivateRoute from './PrivateRoute';
import CircularProgress from '@mui/material/CircularProgress';
import GlobalStyle from './style';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import 'utils/loadFont';
import Saml from 'routes/Saml';

// routes
const Layout = React.lazy(() => import('routes/Layout'));
const Login = React.lazy(() => import('routes/Login'));
const Dashboard = React.lazy(() => import('routes/Dashboard'));
const SendResetPassword = React.lazy(() => import('routes/SendResetPassword'));
const ResetPassword = React.lazy(() => import('routes/ResetPassword'));
const Users = React.lazy(() => import('routes/Users'));
const AuditLog = React.lazy(() => import('routes/AuditLog'));
const Logout = React.lazy(() => import('routes/Logout'));
const Posts = React.lazy(() => import('routes/Posts'));
const Company = React.lazy(() => import('routes/Company'));
const CompanyDetails = React.lazy(() => import('routes/CompanyDetails'));
const CompanySite = React.lazy(() => import('routes/CompanySite'));
const CompanyLocation = React.lazy(() => import('routes/CompanyLocation'));
const Equipment = React.lazy(() => import('routes/Equipment'));
const System = React.lazy(() => import('routes/Systems'));
const Environment = React.lazy(() => import('routes/Environment'));
const Panel = React.lazy(() => import('routes/Panel'));
const Power = React.lazy(() => import('routes/Power'));
const PanelForm = React.lazy(() => import('routes/PanelForm'));
const NotFound = React.lazy(() => import('routes/NotFound'));

// use default theme
// const theme = createTheme();

// Or Create your Own theme:
const theme = createTheme({
  palette: {
    primary: {
      main: 'rgb(22, 84, 135)',
    },
    // secondary: {
    //   main: '#E33E7F',
    // },
  },
});

const App = () => {
  const {
    state: { currentUser, isAuthenticated, loading, error },
    dispatch,
  } = useAuth();

  React.useEffect(() => {
    let ignore = false;
    const fetchUser = async () => {
      dispatch(authAction.sendingRequest({ loading: true }));

      try {
        const payload = await getCurrentUser();
        const { currentUser } = payload.data;

        if (!ignore) {
          dispatch(authAction.setAuth({ currentUser, isAuthenticated: true }));
          dispatch(authAction.sendingRequest({ loading: false }));
        }
      } catch (error) {
        if (!ignore) {
          dispatch(authAction.setAuth({ isAuthenticated: false, currentUser: null, loading: false }));
        }
      }
    };

    if (!currentUser && isAuthenticated) {
      fetchUser();
    }

    return () => {
      ignore = true;
    };
  }, [dispatch, isAuthenticated, currentUser]);

  if (!currentUser && isAuthenticated) {
    return null;
  }

  return (
    <React.Fragment>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <React.Suspense
            fallback={
              <div
                style={{
                  height: '100vh',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </div>
            }
          >
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<SendResetPassword />} />
                <Route path="/reset-password/:accessToken" element={<ResetPassword />} />
                <Route path="/login/saml/st" element={<Saml />} />
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <Layout />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="users" element={<Users />} />
                  <Route path="systems" element={<System />} />
                  <Route path="posts" element={<Posts />} />
                  <Route path="company" element={<Company />} />
                  <Route path="company/:companyId" element={<CompanyDetails />} />
                  <Route path="company/:companyId/:siteId" element={<CompanySite />} />
                  <Route path="company/:companyId/:siteId/:locationId" element={<CompanyLocation />} />
                  <Route path="equipment" element={<Equipment />} />
                  <Route path="panel" element={<Panel />} />
                  <Route path="panel/form" element={<PanelForm />} />
                  <Route path="panel/form/:id" element={<PanelForm />} /> 
                  <Route path="environment" element={<Environment />} />
                  <Route path="power" element={<Power />} />
                  <Route path="audit-log" element={<AuditLog />} />
                  <Route path="logout" element={<Logout />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Router>
          </React.Suspense>
          <GlobalStyle />
        </ThemeProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);
